<script setup lang="ts">
  // Category sales page (§7 plan): 2-level reporting tree (or flat menu
  // rollup) with per-item drill rows, "Sin categoría" surfaced, XLSX export.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useLocalStorage } from '@vueuse/core';
  import StatsService from '@/api/services/stats.service';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsExportButton from '@/components/v2/stats/V2StatsExportButton.vue';
  import V2StatsTable from '@/components/v2/stats/V2StatsTable.vue';
  import { useUi } from '@/composition/use-ui';
  import {
    ICategorySalesResponse,
    ICategorySalesRow,
  } from '@/interfaces/ICategorySalesStats';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IStatsExportColumn } from '@/domain/stats/stats-export';
  import {
    IV2StatsTableColumn,
    IV2StatsTableRow,
  } from '@/components/v2/stats/v2-stats-table.types';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice, round2 } from '@/utils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  type TDimension = 'reporting' | 'menu';

  const dimension = useLocalStorage<TDimension>(
    'v2-category-sales-dimension',
    'reporting'
  );
  const range = ref<IStatsDateRange | null>(null);
  const response = ref<ICategorySalesResponse | null>(null);
  const loading = ref(false);

  // Guards out-of-order responses on rapid range/dimension/branch switches.
  let requestGeneration = 0;

  async function reload() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const result = await statsService.getCategorySales(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        dimension.value,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;
      response.value = result;
    } catch (error) {
      if (generation !== requestGeneration) return;
      ui.serverError(error, i18nT('v2Stats.categorySales.loadError'));
      response.value = null;
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    reload();
  }

  function setDimension(value: TDimension) {
    if (dimension.value === value) return;
    dimension.value = value;
    reload();
  }

  //#region Formatting

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol || '$'
  );

  function money(value: unknown): string {
    if (value === null || typeof value === 'undefined') return '';
    return `${currencySymbol.value}${displayPrice(Number(value), true)}`;
  }

  function pct(value: unknown): string {
    if (value === null || typeof value === 'undefined') return '';
    return `${value}%`;
  }

  function integer(value: unknown): string {
    if (value === null || typeof value === 'undefined') return '';
    return String(value);
  }

  //#endregion

  //#region Table shape (globally-unique synthetic ids for the drill tree)

  function categoryKey(row: ICategorySalesRow, parentKey: string): string {
    const own =
      row.source === 'uncategorized' ? 'uncat' : `${row.source}:${row.id}`;
    return parentKey ? `${parentKey}/${own}` : own;
  }

  /** Item percentages are derived client-side (the API only computes them for
   *  category rows); null when the whole is 0 so the cell renders empty. */
  function pctOf(part: number, whole: number): number | null {
    if (!whole) return null;
    return round2((part / whole) * 100);
  }

  const grandNet = computed(() => response.value?.totals.net ?? 0);

  function toTableRow(
    row: ICategorySalesRow,
    parentKey: string
  ): IV2StatsTableRow {
    const key = categoryKey(row, parentKey);
    const children: IV2StatsTableRow[] = [
      ...(row.children ?? []).map((child) => toTableRow(child, key)),
      ...(row.items ?? []).map(
        (item): IV2StatsTableRow => ({
          id: `${key}/item:${item.itemId}`,
          isItem: true,
          name: item.name ?? i18nT('v2Stats.categorySales.deletedItem'),
          quantity: item.quantity,
          gross: item.gross,
          discountAllocated: item.discountAllocated,
          net: item.net,
          pctOfTotal: pctOf(item.net, grandNet.value),
          pctOfParent: pctOf(item.net, row.net),
        })
      ),
    ];
    return {
      id: key,
      name: row.name,
      quantity: row.quantity,
      gross: row.gross,
      discountAllocated: row.discountAllocated,
      net: row.net,
      pctOfTotal: row.pctOfTotal,
      pctOfParent: parentKey ? row.pctOfParent : null,
      children: children.length ? children : undefined,
    };
  }

  const tableRows = computed<IV2StatsTableRow[]>(() =>
    (response.value?.rows ?? []).map((row) => toTableRow(row, ''))
  );

  const columns = computed<IV2StatsTableColumn[]>(() => [
    {
      key: 'name',
      label: i18nT('v2Stats.categorySales.colName'),
    },
    {
      key: 'quantity',
      label: i18nT('v2Stats.categorySales.colQuantity'),
      numeric: true,
      format: integer,
    },
    {
      key: 'gross',
      label: i18nT('v2Stats.categorySales.colGross'),
      numeric: true,
      format: money,
    },
    {
      key: 'discountAllocated',
      label: i18nT('v2Stats.categorySales.colDiscounts'),
      numeric: true,
      format: money,
    },
    {
      key: 'net',
      label: i18nT('v2Stats.categorySales.colNet'),
      numeric: true,
      format: money,
    },
    {
      key: 'pctOfTotal',
      label: i18nT('v2Stats.categorySales.colPctTotal'),
      numeric: true,
      format: pct,
    },
    {
      key: 'pctOfParent',
      label: i18nT('v2Stats.categorySales.colPctParent'),
      numeric: true,
      format: pct,
    },
  ]);

  const totals = computed<Record<string, unknown> | null>(() => {
    const t = response.value?.totals;
    if (!t || !response.value?.rows.length) return null;
    return {
      name: null,
      quantity: t.quantity,
      gross: t.gross,
      discountAllocated: t.discountAllocated,
      net: t.net,
      pctOfTotal: 100,
      pctOfParent: null,
    };
  });

  //#endregion

  //#region Export (flattened tree with a level column)

  const exportColumns = computed<IStatsExportColumn[]>(() => [
    { key: 'level', header: i18nT('v2Stats.categorySales.colLevel') },
    { key: 'name', header: i18nT('v2Stats.categorySales.colName') },
    {
      key: 'quantity',
      header: i18nT('v2Stats.categorySales.colQuantity'),
      type: 'number',
    },
    {
      key: 'gross',
      header: i18nT('v2Stats.categorySales.colGross'),
      type: 'number',
    },
    {
      key: 'discountAllocated',
      header: i18nT('v2Stats.categorySales.colDiscounts'),
      type: 'number',
    },
    {
      key: 'net',
      header: i18nT('v2Stats.categorySales.colNet'),
      type: 'number',
    },
    {
      key: 'pctOfTotal',
      header: i18nT('v2Stats.categorySales.colPctTotal'),
      type: 'number',
    },
    {
      key: 'pctOfParent',
      header: i18nT('v2Stats.categorySales.colPctParent'),
      type: 'number',
    },
  ]);

  const exportRows = computed<Array<Record<string, unknown>>>(() => {
    const rows: Array<Record<string, unknown>> = [];
    const pushCategory = (row: ICategorySalesRow, depth: number) => {
      rows.push({
        level: i18nT(
          depth === 0
            ? 'v2Stats.categorySales.levelCategory'
            : 'v2Stats.categorySales.levelSubcategory'
        ),
        name: row.name,
        quantity: row.quantity,
        gross: row.gross,
        discountAllocated: row.discountAllocated,
        net: row.net,
        pctOfTotal: row.pctOfTotal,
        pctOfParent: depth > 0 ? row.pctOfParent : null,
      });
      for (const child of row.children ?? []) pushCategory(child, depth + 1);
      for (const item of row.items ?? []) {
        rows.push({
          level: i18nT('v2Stats.categorySales.levelItem'),
          name: item.name ?? i18nT('v2Stats.categorySales.deletedItem'),
          quantity: item.quantity,
          gross: item.gross,
          discountAllocated: item.discountAllocated,
          net: item.net,
          pctOfTotal: pctOf(item.net, grandNet.value),
          pctOfParent: pctOf(item.net, row.net),
        });
      }
    };
    for (const row of response.value?.rows ?? []) pushCategory(row, 0);
    return rows;
  });

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.categorySales.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="category-sales"
        @change="onRangeChange"
      />
    </template>

    <template #actions>
      <div class="dimension-toggle">
        <button
          type="button"
          class="dimension-option"
          :class="{ 'is-active': dimension === 'reporting' }"
          @click="setDimension('reporting')"
        >
          {{ $t('v2Stats.categorySales.dimensionReporting') }}
        </button>
        <button
          type="button"
          class="dimension-option"
          :class="{ 'is-active': dimension === 'menu' }"
          @click="setDimension('menu')"
        >
          {{ $t('v2Stats.categorySales.dimensionMenu') }}
        </button>
      </div>
      <V2StatsExportButton
        :filename="String($t('v2Stats.categorySales.title'))"
        :sheet-name="String($t('v2Stats.categorySales.title'))"
        :columns="exportColumns"
        :rows="exportRows"
        :disabled="loading"
      />
    </template>

    <V2StatsTable
      :columns="columns"
      :rows="tableRows"
      :totals="totals"
      :loading="loading"
      :default-sort="{ key: 'net', desc: true }"
      sticky-first
      :empty-text="String($t('v2Stats.categorySales.empty'))"
    >
      <!-- Item drill rows: % of total is context (muted); % of its category
           renders like any other number. Category rows keep the default
           rendering. -->
      <template #cell-pctOfTotal="{ row }">
        <span v-if="row.isItem" class="item-pct-total">{{
          pct(row.pctOfTotal)
        }}</span>
        <template v-else>{{ pct(row.pctOfTotal) }}</template>
      </template>
    </V2StatsTable>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .item-pct-total {
    @apply tw-text-xs tw-font-normal tw-text-neutral-400;
  }

  .dimension-toggle {
    @apply tw-inline-flex tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

    .dimension-option {
      // h-10 options + track padding = 44px total hit area.
      @apply tw-h-10 tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
